import React, { useEffect, useState } from 'react';
import { Form, Button, Card, Spinner, ProgressBar } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaArrowRight, FaArrowLeft, FaPaperPlane, FaCheckCircle, FaTimesCircle, FaRedo } from 'react-icons/fa';

// Helper para headers de autenticación con JWT
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const API_URL = (process.env.REACT_APP_API_URL || "/").replace(/\/$/, '');

const Evaluacion = ({ moduloId, usuario, onComplete }) => {
  const [preguntas, setPreguntas] = useState([]);
  const [respuestas, setRespuestas] = useState({});
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [enviada, setEnviada] = useState(false);
  const [porcentaje, setPorcentaje] = useState(null);
  const [aprobado, setAprobado] = useState(false);
  const [indiceActual, setIndiceActual] = useState(0);

  useEffect(() => {
    fetch(`${API_URL}/api/capacitaciones/evaluacion/${moduloId}`, {
      headers: getAuthHeaders()
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) setPreguntas(data.data);
        setLoading(false);
      });
  }, [moduloId]);

  const handleChange = (preguntaId, opcion) => {
    setRespuestas(prev => ({ ...prev, [preguntaId]: opcion }));
  };

  const handleNext = () => {
    const preguntaActual = preguntas[indiceActual];
    if (!respuestas[preguntaActual.id]) {
      toast.error('Seleccioná una opción antes de continuar');
      return;
    }
    setIndiceActual(prev => prev + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const incompletas = preguntas.some(p => !respuestas[p.id]);
    if (incompletas) {
      toast.error('Respondé todas las preguntas');
      return;
    }

    // Validar que tengamos los datos necesarios
    if (!moduloId) {
      toast.error('Error: No se pudo identificar el módulo');
      return;
    }

    if (!usuario?.Legajo) {
      toast.error('Error: No se pudo identificar al usuario');
      return;
    }

    const payload = {
      modulo_id: parseInt(moduloId, 10), // Asegurar que sea número
      legajo: parseInt(usuario.Legajo, 10), // Convertir a número
      respuestas: preguntas.map(p => ({
        pregunta_id: parseInt(p.id, 10), // Asegurar que sea número
        respuesta: respuestas[p.id]
      }))
    };

    console.log('📤 Enviando evaluación:', payload); // Debug

    setEnviando(true);
    
    try {
      const res = await fetch(`${API_URL}/api/capacitaciones/evaluacion/responder`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      
      if (!res.ok || !data.success) {
        console.error('❌ Error del servidor:', data);
        throw new Error(data.error || 'Error desconocido');
      }

      const esAprobado = data.porcentaje >= 60;
      setEnviada(true);
      setPorcentaje(data.porcentaje);
      setAprobado(esAprobado);
      
      // Callback al componente padre
      if (onComplete) {
        onComplete(esAprobado, data.porcentaje);
      }
      
      toast.success('✅ Evaluación registrada');
    } catch (err) {
      console.error('❌ Error al enviar:', err);
      toast.error('❌ Error al guardar respuestas');
    } finally {
      setEnviando(false);
    }
  };



  if (loading) return (
    <div className="text-center py-4">
      <Spinner animation="border" variant="primary" size="sm" />
      <p className="text-muted mt-2 mb-0">Cargando preguntas...</p>
    </div>
  );

  if (preguntas.length === 0) return (
    <div className="text-center py-4">
      <p className="text-muted mb-0">Este módulo no tiene preguntas cargadas.</p>
    </div>
  );

  const preguntaActual = preguntas[indiceActual];
  const progreso = ((indiceActual + 1) / preguntas.length) * 100;
  const respuestaSeleccionada = respuestas[preguntaActual.id];

  // Resultado final
  if (enviada) {
    return (
      <div className="resultado-evaluacion text-center py-3">
        <div className={`resultado-icon mb-3 ${aprobado ? 'aprobado' : 'reprobado'}`}>
          {aprobado ? (
            <FaCheckCircle size={50} className="text-success" />
          ) : (
            <FaTimesCircle size={50} className="text-danger" />
          )}
        </div>
        
        <h5 className={aprobado ? 'text-success' : 'text-danger'}>
          {aprobado ? '¡Felicitaciones!' : 'No aprobaste'}
        </h5>
        
        <div className="score-result my-3">
          <span className={`score-big ${aprobado ? 'text-success' : 'text-danger'}`}>
            {porcentaje}%
          </span>
        </div>
        
        <ProgressBar 
          now={porcentaje} 
          variant={aprobado ? 'success' : 'danger'} 
          style={{ height: '8px' }}
          className="mb-3"
        />
        
        <p className="text-muted">
          {aprobado 
            ? 'Tu resultado ha sido registrado correctamente. Cerrá esta ventana para descargar tu certificado.'
            : 'Necesitás al menos 60% para aprobar. ¡Intentalo de nuevo!'
          }
        </p>
        
        {!aprobado && (
          <Button 
            variant="outline-primary"
            onClick={() => {
              setRespuestas({});
              setIndiceActual(0);
              setEnviada(false);
              setPorcentaje(null);
              setAprobado(false);
            }}
          >
            <FaRedo className="me-2" />
            Reintentar
          </Button>
        )}
      </div>
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      {/* Barra de progreso */}
      <div className="evaluacion-progress mb-3">
        <div className="pregunta-counter">
          Pregunta {indiceActual + 1} de {preguntas.length}
        </div>
        <ProgressBar 
          now={progreso} 
          variant="primary"
          style={{ height: '6px' }}
        />
      </div>

      {/* Pregunta actual */}
      <Card className="pregunta-card border-0 shadow-sm">
        <div className="pregunta-header">
          <span className="pregunta-numero">{indiceActual + 1}</span>
          <span className="pregunta-texto">{preguntaActual.pregunta}</span>
        </div>
        
        <div className="opciones-container">
          {preguntaActual.tipo === 'vf' ? (
            <>
              <div 
                className={`opcion-item ${respuestaSeleccionada === 'true' ? 'selected' : ''}`}
                onClick={() => handleChange(preguntaActual.id, 'true')}
              >
                <Form.Check
                  type="radio"
                  name={`pregunta-${preguntaActual.id}`}
                  label="Verdadero"
                  checked={respuestaSeleccionada === 'true'}
                  onChange={() => handleChange(preguntaActual.id, 'true')}
                />
              </div>
              <div 
                className={`opcion-item ${respuestaSeleccionada === 'false' ? 'selected' : ''}`}
                onClick={() => handleChange(preguntaActual.id, 'false')}
              >
                <Form.Check
                  type="radio"
                  name={`pregunta-${preguntaActual.id}`}
                  label="Falso"
                  checked={respuestaSeleccionada === 'false'}
                  onChange={() => handleChange(preguntaActual.id, 'false')}
                />
              </div>
            </>
          ) : (
            ['a', 'b', 'c', 'd'].map(op => (
              <div 
                key={op}
                className={`opcion-item ${respuestaSeleccionada === op ? 'selected' : ''}`}
                onClick={() => handleChange(preguntaActual.id, op)}
              >
                <Form.Check
                  type="radio"
                  name={`pregunta-${preguntaActual.id}`}
                  label={
                    <span>
                      <strong className="me-2">{op.toUpperCase()})</strong>
                      {preguntaActual[`opcion_${op}`]}
                    </span>
                  }
                  checked={respuestaSeleccionada === op}
                  onChange={() => handleChange(preguntaActual.id, op)}
                />
              </div>
            ))
          )}
        </div>
      </Card>

      {/* Navegación */}
      <div className="evaluacion-nav mt-3">
        {indiceActual > 0 && (
          <Button 
            variant="outline-secondary"
            onClick={() => setIndiceActual(prev => prev - 1)}
          >
            <FaArrowLeft className="me-1" /> Anterior
          </Button>
        )}
        
        {indiceActual < preguntas.length - 1 ? (
          <Button 
            variant="primary"
            onClick={handleNext}
            disabled={!respuestaSeleccionada}
            className="ms-auto"
          >
            Siguiente <FaArrowRight className="ms-1" />
          </Button>
        ) : (
          <Button 
            type="submit"
            variant="success"
            disabled={!respuestaSeleccionada || enviando}
            className="ms-auto"
          >
            {enviando ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Enviando...
              </>
            ) : (
              <>
                <FaPaperPlane className="me-2" />
                Enviar evaluación
              </>
            )}
          </Button>
        )}
      </div>
    </Form>
  );
};

export default Evaluacion;
